import React, { useState } from 'react'
import { AlertCircle, Plus, X } from 'lucide-react'

const AccountStep = ({ data, onChange, onNext }) => {
  const [agents, setAgents] = useState(data.agents || [])

  const addAgent = () => {
    const newAgent = { id: Date.now(), name: '', email: '' }
    setAgents([...agents, newAgent])
  }

  const removeAgent = (id) => {
    setAgents(agents.filter(agent => agent.id !== id))
  }

  const updateAgent = (id, field, value) => {
    setAgents(agents.map(agent => 
      agent.id === id ? { ...agent, [field]: value } : agent
    ))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onChange({ agents })
    onNext()
  }

  const updateField = (field, value) => {
    onChange({ [field]: value })
  }

  return (
    <div className="view active">
      <div className="panel">
        <div className="panel-head" style={{
          background: 'linear-gradient(135deg,var(--navy) 0%,var(--navy3) 100%)',
          padding: '32px 36px 28px',
          borderBottom: '3px solid var(--gold)'
        }}>
          <div className="panel-title" style={{
            fontFamily: 'var(--font-d)',
            fontSize: '26px',
            color: 'var(--white)',
            marginBottom: '6px'
          }}>
            Create Your IPST Account
          </div>
          <div className="panel-subtitle" style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.55)',
            fontWeight: '300'
          }}>
            Your secure portal to register your NIL on the Story Protocol blockchain and connect to IPST tracking stock.
          </div>
        </div>
        
        <div className="panel-body" style={{ padding: '36px' }}>
          {/* Security notice */}
          <div className="info-bar" style={{
            background: 'rgba(13,158,124,0.07)',
            border: '1px solid rgba(13,158,124,0.2)',
            borderRadius: 'var(--r)',
            padding: '14px 18px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}>
            <div className="icon" style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>🔒</div>
            <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6' }}>
              All information is encrypted at rest and in transit. Only you, your approved agents, and IPST compliance staff can access your account. 
              <strong> IPST does not share your personal information with third parties.</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Personal information
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid-3" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '16px'
            }}>
              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Legal first name <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  placeholder="As it appears on your ID"
                  required
                />
              </div>

              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Middle name
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.middleName}
                  onChange={(e) => updateField('middleName', e.target.value)}
                  placeholder="Middle name or initial"
                />
              </div>

              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Legal last name <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  placeholder="As it appears on your ID"
                  required
                />
              </div>
            </div>

            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Date of birth <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="date"
                  value={data.dateOfBirth}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                  required
                />
              </div>

              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Phone number <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="tel"
                  value={data.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  required
                />
              </div>

              <div className="fg form-full" style={{ gridColumn: '1/-1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Email address <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="email"
                  value={data.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            {/* Mailing Address */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Mailing address
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="fg form-full" style={{ gridColumn: '1/-1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Street address <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="123 Main St, Apt 4B"
                  required
                />
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  City <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="City"
                  required
                />
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  State <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <select
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  value={data.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  required
                >
                  <option value="">Select state…</option>
                  <option>AL</option><option>AK</option><option>AZ</option><option>AR</option><option>CA</option>
                  <option>CO</option><option>CT</option><option>DE</option><option>FL</option><option>GA</option>
                  <option>HI</option><option>ID</option><option>IL</option><option>IN</option><option>IA</option>
                  <option>KS</option><option>KY</option><option>LA</option><option>ME</option><option>MD</option>
                  <option>MA</option><option>MI</option><option>MN</option><option>MS</option><option>MO</option>
                  <option>MT</option><option>NE</option><option>NV</option><option>NH</option><option>NJ</option>
                  <option>NM</option><option>NY</option><option>NC</option><option>ND</option><option>OH</option>
                  <option>OK</option><option>OR</option><option>PA</option><option>RI</option><option>SC</option>
                  <option>SD</option><option>TN</option><option>TX</option><option>UT</option><option>VT</option>
                  <option>VA</option><option>WA</option><option>WV</option><option>WI</option><option>WY</option>
                </select>
              </div>

              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  ZIP code <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  placeholder="00000"
                  maxLength="10"
                  required
                />
              </div>
            </div>

            {/* Approved Agents */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Approved agents & representatives
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="warn-bar" style={{
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 'var(--r)',
              padding: '14px 18px',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <div className="icon" style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</div>
              <p style={{ fontSize: '13px', color: 'var(--text2)' }}>
                Agents and representatives listed here will have read-only access to your account. They cannot authorize transactions or modify your NIL without your explicit two-factor approval.
              </p>
            </div>

            <div id="agent-list" style={{ marginBottom: '10px' }}>
              {agents.map(agent => (
                <div key={agent.id} className="agent-row" style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 1.5fr 1fr auto',
                  gap: '16px',
                  alignItems: 'end',
                  marginBottom: '16px'
                }}>
                  <div className="fg" style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text2)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: '6px'
                    }}>
                      Agent / Representative name
                    </label>
                    <input
                      className="fi"
                      style={{
                        width: '100%',
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--r)',
                        padding: '11px 14px',
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: 'var(--text)',
                        background: 'var(--white)',
                        transition: 'border-color 0.2s, box-shadow 0.2s'
                      }}
                      type="text"
                      placeholder="Full legal name"
                      value={agent.name}
                      onChange={(e) => updateAgent(agent.id, 'name', e.target.value)}
                    />
                  </div>
                  <div className="fg" style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text2)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: '6px'
                    }}>
                      Email
                    </label>
                    <input
                      className="fi"
                      style={{
                        width: '100%',
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--r)',
                        padding: '11px 14px',
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: 'var(--text)',
                        background: 'var(--white)',
                        transition: 'border-color 0.2s, box-shadow 0.2s'
                      }}
                      type="email"
                      placeholder="agent@agency.com"
                      value={agent.email}
                      onChange={(e) => updateAgent(agent.id, 'email', e.target.value)}
                    />
                  </div>
                  <div className="fg" style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text2)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: '6px'
                    }}>
                      Role
                    </label>
                    <select
                      className="fi"
                      style={{
                        width: '100%',
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--r)',
                        padding: '11px 14px',
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: 'var(--text)',
                        background: 'var(--white)',
                        transition: 'border-color 0.2s, box-shadow 0.2s'
                      }}
                      value={agent.role}
                      onChange={(e) => updateAgent(agent.id, 'role', e.target.value)}
                    >
                      <option value="Agent">Agent</option>
                      <option value="Attorney">Attorney</option>
                      <option value="Family member">Family member</option>
                      <option value="Financial advisor">Financial advisor</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn-rm"
                    onClick={() => removeAgent(agent.id)}
                    style={{
                      background: 'none',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--r)',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      color: 'var(--text3)',
                      fontSize: '13px',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-outline-gold btn-sm"
              onClick={addAgent}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '9px 18px',
                borderRadius: 'var(--r)',
                fontFamily: 'var(--font-b)',
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.02em',
                cursor: 'pointer',
                border: '1.5px solid var(--gold)',
                background: 'transparent',
                color: 'var(--gold)',
                transition: 'all 0.2s'
              }}
            >
              + Add agent or representative
            </button>

            {/* Account Security */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Account security
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Password <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="password"
                  value={data.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder="Minimum 12 characters"
                  required
                />
                <div className="field-hint" style={{
                  fontSize: '11px',
                  color: 'var(--text3)',
                  marginTop: '5px'
                }}>
                  Must include uppercase, lowercase, number, and symbol.
                </div>
              </div>
              <div className="fg" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Confirm password <span className="req" style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="password"
                  value={data.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  placeholder="Repeat password"
                  required
                />
              </div>
            </div>

            {/* Story Protocol Wallet Section */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Story Protocol wallet — on-chain identity
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div style={{
              background: 'var(--navy)',
              borderRadius: 'var(--r2)',
              padding: '22px 24px',
              display: 'flex',
              gap: '18px',
              alignItems: 'flex-start'
            }}>
              <div style={{ fontSize: '28px', flexShrink: 0, marginTop: '2px' }}>⛓️</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '700',
                  color: 'var(--gold2)',
                  marginBottom: '8px'
                }}>
                  IPST Master Wallet — All NIL Token Activity
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.65)',
                  lineHeight: '1.7',
                  marginBottom: '14px'
                }}>
                  All Story Protocol IP Account (IPA) registrations, PIL sub-license minting, and oracle royalty vault operations are executed through the 
                  <strong style={{ color: 'var(--white)' }}> IPST Master Wallet</strong> — a multi-signature institutional wallet managed by IP Strategy Holdings, Inc. (Nasdaq: IPST). 
                  You do not need a personal blockchain wallet. IPST handles all on-chain transactions on your behalf as your registered IP agent.
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '10px'
                }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 'var(--r)',
                    padding: '12px'
                  }}>
                    <div style={{
                      fontSize: '10px',
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.09em',
                      marginBottom: '5px'
                    }}>
                      Wallet type
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--white)',
                      fontWeight: '600'
                    }}>
                      Multi-sig institutional
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 'var(--r)',
                    padding: '12px'
                  }}>
                    <div style={{
                      fontSize: '10px',
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.09em',
                      marginBottom: '5px'
                    }}>
                      Key custody
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--white)',
                      fontWeight: '600'
                    }}>
                      IPST HSM · BitGo
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 'var(--r)',
                    padding: '12px'
                  }}>
                    <div style={{
                      fontSize: '10px',
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.09em',
                      marginBottom: '5px'
                    }}>
                      Network
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#4ade80',
                      fontWeight: '600'
                    }}>
                      Story Protocol · Chain 1514
                    </div>
                  </div>
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  background: 'rgba(13,158,124,0.12)',
                  border: '1px solid rgba(13,158,124,0.25)',
                  borderRadius: 'var(--r)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '4px'
                  }}>
                    IPST Master Wallet Address
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-m)',
                    fontSize: '12px',
                    color: '#4ade80'
                  }}>
                    0x4953545f4d41535445525f57414c4c45545f494e43
                  </div>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <div className="btn-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '28px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border)'
            }}>
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '13px 28px',
                  borderRadius: 'var(--r)',
                  fontFamily: 'var(--font-b)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'all 0.2s',
                  background: 'var(--gold)',
                  color: 'var(--navy)',
                  boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
                }}
              >
                Continue to Sport & School →
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AccountStep
